import React from "react";
import PropTypes from "prop-types";
import { FormGroup, Label, Input, FormFeedback, FormText, InputGroup, InputGroupText } from "reactstrap";
import Flatpickr from "react-flatpickr";
import { Switch } from "./Switch";

// flatpickr 回傳 Date[] → 全站一致的 YYYY-MM-DD(本地時區,不用 toISOString)
const toYmd = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * 受控表單欄位 primitives（design system）。
 * 與 Formik 無關，用 value/onChange 驅動，可獨立用於自訂版面；
 * 需要整套表單時用 CrudFormModal（config 驅動）。
 *
 *   <TextField label="姓名" required value={v} onChange={setV} error={err} />
 *   <SelectField label="角色" options={roles} value={r} onChange={setR} />
 *   <SwitchField label="啟用" checked={on} onChange={setOn} />
 */
/** 共用的欄位 props 契約(新欄位元件沿用,別各自定義一套) */
export const fieldBaseProps = {
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  error: PropTypes.string,
  help: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
};

/** 欄位外殼:label + 內容 + 錯誤/說明(新欄位元件重用,別重刻) */
export const FieldShell = ({ label, required, error, help, children, htmlFor }) => (
  <FormGroup>
    {label ? (
      // htmlFor 有給才綁得到欄位 —— 沒綁的話讀螢幕的人聽不出這個框是什麼,
      // 點標籤也不會 focus 到欄位
      <Label className="form-label" for={htmlFor}>
        {label}{required ? <span className="text-danger"> ＊</span> : null}
      </Label>
    ) : null}
    {children}
    {error ? <FormFeedback className="d-block">{error}</FormFeedback> : null}
    {help && !error ? <FormText>{help}</FormText> : null}
  </FormGroup>
);
FieldShell.propTypes = {
  htmlFor: PropTypes.string,
  // 可以是元素(例:起迄U 括號段粗體);元素時呼叫端要另給純文字的無障礙名稱
  label: PropTypes.node, required: PropTypes.bool,
  error: PropTypes.string, help: PropTypes.string, children: PropTypes.node,
};

const emit = (onChange) => (e) => onChange && onChange(e.target.value, e);

export const TextField = ({ type = "text", label, name, value = "", onChange, required, error, help, disabled, placeholder, prefix, suffix, suffixSelect, ...rest }) => (
  <FieldShell label={label} required={required} error={error} help={help} htmlFor={name}>
    {prefix || suffix || suffixSelect ? (
      // 前/後綴走模板既有的 input-group(/form-elements):
      // 購買金額「NT$」(2196:49139)、CPU「Core」/虛擬機「台」(2128:26341);
      // suffixSelect=可切換單位的後綴下拉(RAM GB⌄/硬碟 TB⌄,issue 10)
      <InputGroup>
        {prefix ? <InputGroupText>{prefix}</InputGroupText> : null}
        <Input id={name} type={type} name={name} value={value} onChange={emit(onChange)} invalid={!!error} disabled={disabled} placeholder={placeholder} {...rest} />
        {suffix ? <InputGroupText>{suffix}</InputGroupText> : null}
        {suffixSelect ? (
          <Input
            type="select" className="flex-grow-0 w-auto" aria-label={`${label}單位`}
            name={suffixSelect.name} value={suffixSelect.value} disabled={disabled}
            onChange={(e) => suffixSelect.onChange && suffixSelect.onChange(e.target.value)}
          >
            {(suffixSelect.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </Input>
        ) : null}
      </InputGroup>
    ) : (
      <Input id={name} type={type} name={name} value={value} onChange={emit(onChange)} invalid={!!error} disabled={disabled} placeholder={placeholder} {...rest} />
    )}
  </FieldShell>
);
TextField.propTypes = {
  ...fieldBaseProps, type: PropTypes.string, prefix: PropTypes.node, suffix: PropTypes.node,
  suffixSelect: PropTypes.shape({
    name: PropTypes.string, value: PropTypes.string, onChange: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.string),
  }),
};

export const NumberField = (props) => <TextField type="number" {...props} />;
/**
 * 單日選擇(issue 01):模板 /form-advanced 的 flatpickr(Auto Close),
 * 對外 API 不變(value=ISO 字串、onChange(value))。設計稿:placeholder「年/月/日」+日曆 icon。
 * allowInput 保留鍵盤輸入(測試與無障礙),失焦時由 flatpickr 解析。
 * 日曆樣式走 scss 鏈(同 DateRangeField),不 import 廠商 css。
 */
export const DateField = ({ label, name, value = "", onChange, required, error, help, disabled, placeholder = "年/月/日", ...rest }) => (
  <FieldShell label={label} required={required} error={error} help={help} htmlFor={name}>
    {/* onChange 掛在 InputGroup 攔冒泡的 native change:allowInput 手打(含測試的
        userEvent.type)走這條;月曆點選走 Flatpickr 自己的 onChange,兩條殊途同值。
        只在「完整日期」時回寫 —— 逐鍵回寫會讓 flatpickr 每鍵重解析(半截字串
        還會被判 Invalid date),把輸入拖到逾時 */}
    <InputGroup onChange={(e) => {
      const v = e.target.value;
      if (onChange && (!v || /^\d{4}-\d{2}-\d{2}$/.test(v))) onChange(v);
    }}>
      <Flatpickr
        id={name}
        name={name}
        className={`form-control${error ? " is-invalid" : ""}`}
        aria-label={label}
        placeholder={placeholder}
        disabled={disabled}
        value={value || ""}
        options={{ dateFormat: "Y-m-d", allowInput: true }}
        onChange={(dates, str) => onChange && onChange(str || (dates[0] ? toYmd(dates[0]) : ""))}
        {...rest}
      />
      <InputGroupText><i className="bx bx-calendar-event" /></InputGroupText>
    </InputGroup>
  </FieldShell>
);
DateField.propTypes = { ...fieldBaseProps };
export const EmailField = (props) => <TextField type="email" {...props} />;
export const PasswordField = (props) => <TextField type="password" {...props} />;

export const TextareaField = ({ label, name, value = "", onChange, required, error, help, disabled, placeholder, rows = 3, ...rest }) => (
  <FieldShell label={label} required={required} error={error} help={help} htmlFor={name}>
    <Input id={name} type="textarea" rows={rows} name={name} value={value} onChange={emit(onChange)} invalid={!!error} disabled={disabled} placeholder={placeholder} {...rest} />
  </FieldShell>
);
TextareaField.propTypes = { ...fieldBaseProps, rows: PropTypes.number };

export const SelectField = ({ label, name, value = "", onChange, required, error, help, disabled, options = [], placeholder = "請選擇", ...rest }) => (
  <FieldShell label={label} required={required} error={error} help={help} htmlFor={name}>
    <Input id={name} type="select" name={name} value={value} onChange={emit(onChange)} invalid={!!error} disabled={disabled} {...rest}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </Input>
  </FieldShell>
);
SelectField.propTypes = { ...fieldBaseProps, options: PropTypes.array };

// 開關走模板 /form-advanced 的 CSS Switch(見 ui/Switch.js)
export const SwitchField = ({ label, name, checked = false, onChange, help, disabled }) => (
  <FormGroup className="d-flex align-items-center gap-2">
    <Switch id={name} text={label} checked={checked} disabled={disabled} onChange={onChange} />
    {help ? <FormText className="ms-2">{help}</FormText> : null}
  </FormGroup>
);
SwitchField.propTypes = {
  label: PropTypes.string, name: PropTypes.string,
  checked: PropTypes.bool, onChange: PropTypes.func, help: PropTypes.string, disabled: PropTypes.bool,
};
